/**
 * §10.8 Session guard — a single global 401 interceptor.
 *
 * The app makes authenticated API calls from ~70 sites, none of which reacted
 * to a 401. So when a session became invalid server-side (e.g. the account was
 * suspended/frozen, or the stored token stopped working), the user was left on
 * empty screens instead of being returned to login. This wraps `window.fetch`
 * once and, on a 401 from an authenticated API request, dispatches a single
 * `auth:unauthorized` event that AuthContext listens for to clear the session.
 *
 * Auth endpoints are exempt: a 401 from login/refresh/etc. is expected there
 * (wrong password, invalid refresh token) and is handled by those flows — it
 * must not trigger a spurious "your session ended" logout.
 */

export const UNAUTHORIZED_EVENT = 'auth:unauthorized'

// Endpoints where a 401 is a normal, expected outcome (not a dead session).
const AUTH_EXEMPT = /\/api\/v1\/auth\/(login|register|refresh|forgot_password|reset_password|verify_email|social|email_check)\b/

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url
  return String(input ?? '')
}

/**
 * Wrap a fetch implementation so a 401 on an authenticated API request emits
 * `UNAUTHORIZED_EVENT`. Pure and injectable so it can be unit-tested. The
 * response is always returned untouched — the guard never changes behavior for
 * callers, it only observes.
 */
export function installSessionGuard(fetchImpl: typeof fetch): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await fetchImpl(input, init)
    try {
      if (response.status === 401) {
        const url = urlOf(input)
        if (url.includes('/api/') && !AUTH_EXEMPT.test(url) && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT))
        }
      }
    } catch {
      /* never let the guard break a real response */
    }
    return response
  }
}

let activated = false

/** Install the guard on `window.fetch` exactly once (no-op off the browser). */
export function activateSessionGuard(): void {
  if (activated || typeof window === 'undefined' || typeof window.fetch !== 'function') return
  window.fetch = installSessionGuard(window.fetch.bind(window))
  activated = true
}
